import React, { useState } from "react";
import "./Charts.css";

const SIZE = 200;
const STROKE = 30;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// A 2px surface gap keeps adjacent segments from reading as one mark.
const GAP = 2;

/**
 * Ring chart for a part-to-whole split, with the total as the hero number in
 * the middle. `segments` is [{ label, value, share, display, color }].
 */
export default function DonutChart({ segments, total, totalLabel = "Total" }) {
  const [active, setActive] = useState(null);
  const sum = segments.reduce((acc, item) => acc + item.value, 0) || 1;

  let offset = 0;

  return (
    <div className="donut">
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="donut__svg"
        role="img"
        aria-label={`${totalLabel} ${total}. ${segments
          .map((s) => `${s.label} ${s.share}%`)
          .join(", ")}`}
      >
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {segments.map((segment) => {
            const length = (segment.value / sum) * CIRCUMFERENCE;
            const dash = Math.max(length - GAP, 0);
            const circle = (
              <circle
                key={segment.label}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={segment.color}
                strokeWidth={active === segment.label ? STROKE + 5 : STROKE}
                strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                strokeDashoffset={-offset}
                className="donut__segment"
                onMouseEnter={() => setActive(segment.label)}
                onMouseLeave={() => setActive(null)}
              />
            );
            offset += length;
            return circle;
          })}
        </g>

        <text
          x="50%"
          y="47%"
          textAnchor="middle"
          className="donut__total"
          dominantBaseline="middle"
        >
          {total}
        </text>
        <text
          x="50%"
          y="60%"
          textAnchor="middle"
          className="donut__total-label"
          dominantBaseline="middle"
        >
          {totalLabel}
        </text>
      </svg>

      <ul className="donut__legend">
        {segments.map((segment) => (
          <li
            key={segment.label}
            className={`donut__legend-item${
              active === segment.label ? " donut__legend-item--active" : ""
            }`}
            onMouseEnter={() => setActive(segment.label)}
            onMouseLeave={() => setActive(null)}
          >
            <span
              className="donut__swatch"
              style={{ background: segment.color }}
            />
            <span className="donut__legend-label">{segment.label}</span>
            {/* Segments may override the default "NN% (value)" legend text. */}
            <span className="donut__legend-share">
              {segment.legendPrimary ?? `${segment.share}%`}
            </span>
            <span className="donut__legend-value">
              {segment.legendSecondary ?? `(${segment.display})`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
